import React, { useState, useRef, useEffect } from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Table,
  Checkbox,
  Badge,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "reactstrap"
import "./Contract.scss"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Fn_FillListData, Fn_AddEditData, Fn_DeleteData } from "store/Functions"
import { API_WEB_URLS } from "constants/constAPI"
import { toast } from "react-toastify"
import { getGlobalOptions } from "helpers/GlobalOptionsHelper"

// Helper function to format date from ISO to yyyy-MM-dd
const formatDateForInput = dateString => {
  if (!dateString) return ""
  if (typeof dateString === "string" && dateString.includes("T")) {
    return dateString.split("T")[0]
  }
  if (dateString instanceof Date) {
    return dateString.toISOString().split("T")[0]
  }
  return dateString
}

// Helper function to convert API date format (DD-MM-YYYY) to input format (YYYY-MM-DD)
const convertApiDateToInputFormat = (apiDate) => {
  if (!apiDate) return '';
  const parts = apiDate.split('-');
  if (parts.length === 3) {
    // Check if already in YYYY-MM-DD format (first part has 4 digits)
    if (parts[0].length === 4) {
      // Already in YYYY-MM-DD format, return as is
      return apiDate;
    }
    // Convert DD-MM-YYYY to YYYY-MM-DD
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return apiDate;
}

// Helper function to convert input date format (YYYY-MM-DD) to API format (DD-MM-YYYY)
const convertInputDateToApiFormat = (inputDate) => {
  if (!inputDate) return '';
  const parts = inputDate.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert YYYY-MM-DD to DD-MM-YYYY
  }
  return inputDate;
}

// Helper function to convert date to SQL Server format (YYYY-MM-DD)
const convertDateToSQLServerFormat = (inputDate) => {
  if (!inputDate) return '';
  // If already in YYYY-MM-DD format, return as is (SQL Server compatible)
  if (typeof inputDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(inputDate)) {
    return inputDate;
  }
  // If in DD-MM-YYYY format, convert to YYYY-MM-DD
  const parts = inputDate.split('-');
  if (parts.length === 3) {
    // Check if it's DD-MM-YYYY or YYYY-MM-DD
    if (parts[0].length === 4) {
      // Already YYYY-MM-DD
      return inputDate;
    } else {
      // DD-MM-YYYY, convert to YYYY-MM-DD
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
  }
  // Try to parse as Date object
  try {
    const dateObj = new Date(inputDate);
    if (!isNaN(dateObj.getTime())) {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {
    // If parsing fails, return as is
  }
  return inputDate;
}

// Helper function to format date in "DD Month YYYY" format
const formatDateForDisplay = dateString => {
  if (!dateString) return ""
  
  let dateObj
  if (dateString instanceof Date) {
    dateObj = dateString
  } else if (typeof dateString === "string") {
    if (dateString.includes("T")) {
      dateObj = new Date(dateString.split("T")[0])
    } else {
      dateObj = new Date(dateString)
    }
  } else {
    return ""
  }
  
  if (isNaN(dateObj.getTime())) return ""
  
  const months = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"]
  
  const day = dateObj.getDate()
  const month = months[dateObj.getMonth()]
  const year = dateObj.getFullYear()
  
  return `${day} ${month} ${year}`
}

// Helper function to create default lifting rows
const createDefaultLiftingRows = (count = 7) => {
  return Array(count)
    .fill()
    .map(() => ({ Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" }))
}

const EditContract = ({
  isModal = false,
  contractData = null,
  onClose = null,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Refs for focus management
  const contractNoRef = useRef(null)
  const contractNoSelectRef = useRef(null)
  const dateRef = useRef(null)
  const broContNoRef = useRef(null)
  const broContDateRef = useRef(null)
  const partyRef = useRef(null)
  const brokerRef = useRef(null)
  const commodityRef = useRef(null)
  const qtyRef = useRef(null)
  const unitRef = useRef(null)
  const rateRef = useRef(null)
  const importDutyRef = useRef(null)
  const tariffRef = useRef(null)
  const exchangeRateRef = useRef(null)
  const calPerTonDutyRef = useRef(null)
  const contractTypeRef = useRef(null)
  const shipMonthRef = useRef(null)
  const shipYearRef = useRef(null)
  const vesselRef = useRef(null)
  const deliveryPortRef = useRef(null)
  const shipmentFromRef = useRef(null)
  const shipmentToRef = useRef(null)
  const liftedFromRef = useRef(null)
  const liftedToRef = useRef(null)
  const remarksRef = useRef(null)
  const advPaymentRef = useRef(null)
  const pmtRef = useRef(null)
  const advDateRef = useRef(null)
  const note6Ref = useRef(null)
  const note1Ref = useRef(null)
  const note2Ref = useRef(null)
  const note3Ref = useRef(null)
  const note4Ref = useRef(null)
  const note5Ref = useRef(null)
  const saveButtonRef = useRef(null)
  const [state, setState] = useState({
    id: 0,
    Date: new Date().toISOString().split("T")[0],
    Date1: new Date().toISOString().split("T")[0],
    ContractNo: "",
    BroContNo: "",
    BroContDate: "",
    AdvDate: "",
    DiffAmt: 0,
    AdvPayment: "",
    F_PartyLedger: 0,
    F_BrokerLedger: 0,
    F_ItemType: 0,
    Note5: "",
    Note6: "",
    Note1: "",
    Note2: "",
    Note3: "",
    Note4: "",
    Qty: 0,
    Rate: 0,
    Rate1: 0,
    F_UnitMaster: 0,
    F_MonthMaster: 0,
    F_YearMaster: 0,
    Vessel: "",
    DeliveryPort: "",
    F_ContractH: "",
    ShipMentFromDate: null,
    ShipMentToDate: null,
    LiftedFromDate: null,
    LiftedToDate: null,
    F_ContractLedger: 0,
    Remarks: "",
    LorryNo: "",
    BNo: "",
    Lifted: "",
    AvgRate: 0,
    InvRate: 0,
    askForPrint: false,
    pdfPrint: false,
    pending: "",
    pmt: "",
    lifting: "",
    avgRate: "",
    LiftingLedger: 0,
    BackSellerCNo: "",
    importDuty: 0,
    tariff: 0,
    exchangeRate: 0,
    calPerTonDuty: 0,
    Type: 1, // 1 for Buy, 2 for Sell
    liftingData: [],
    ContractHNo: [],
    PartyAccountArray: [],
    BrokerAccountArray: [],
    TaxAccountArray: [],
    PartyAccountArray1: [],
    BrokerAccountArray2: [],
    TaxAccountArray1: [],
    UnitArray: [],
    ItemArray: [],
    MonthArray: [],
    YearArray: [],
    GlobalArray: [],
    ContractArray: [],
    NewArray: [],
    LiftingArray: [],
    formData: {},
    isProgress: false,
    showSuccessMessage: false,
    showErrorMessage: false,
    message: "",
    isEditMode: false,
    // New state variables for lifting validation and calculations
    totalLiftingQty: 0,
    contractQty: 0,
    showLiftingAlert: false,
    liftingAlertMessage: "",
    DiffAmt: "0.00",
    // New state variable to control field disable state
    isFieldsDisabled: false,
    DoDetails: {}, // Object to store detailed contract information
    DoDetailsArray: [], // Array for DO details entries
  })
  const [liftingRows, setLiftingRows] = useState(createDefaultLiftingRows(7))
  
  // DoDetails modal states
  const [doDetailsModal, setDoDetailsModal] = useState(false)
  const [commodityModal, setCommodityModal] = useState(false)
  const [newCommodityData, setNewCommodityData] = useState({ Name: "", Alias: "" })
  const [editingRowIndex, setEditingRowIndex] = useState(null)
  const doNoInputRef = useRef(null)

  const API_URL_SAVE = `${API_WEB_URLS.ContractH}/0/token`
  const API_URL_Lifting = `${API_WEB_URLS.AddLifting}/0/token`
  const API_URL = API_WEB_URLS.MASTER + "/0/token/PartyAccount"
  const API_URLBroker = API_WEB_URLS.MASTER + "/0/token/BrokerAccount"
  const API_URL1 = API_WEB_URLS.MASTER + "/0/token/TaxAccount"
  const API_URL2 = API_WEB_URLS.MASTER + "/0/token/UnitMaster"
  const API_URL3 = API_WEB_URLS.MASTER + "/0/token/ItemMaster"
  const API_URL4 = API_WEB_URLS.MASTER + "/0/token/MonthMaster"
  const API_URL4Year = API_WEB_URLS.MASTER + "/0/token/YearMaster"
  // const API_URL5 = API_WEB_URLS.MASTER + "/0/token/UpdateGlobalOptions" // Now using localStorage
  const API_URL6 = API_WEB_URLS.MASTER + "/0/token/PartyAccount1"
  const API_URL7 = API_WEB_URLS.MASTER + "/0/token/ContractHNoWeb"
  const API_URL8 = API_WEB_URLS.MASTER + "/0/token/ContractHMaster"
  const API_URL9 = API_WEB_URLS.MASTER + "/0/token/ContractNoNew"
  const API_URL10 = API_WEB_URLS.MASTER + "/0/token/LiftingMaster"
  const API_URL12 = API_WEB_URLS.MASTER + "/0/token/DoDetailsByContract"
  const API_URL_DO_DETAILS = API_WEB_URLS.MASTER + "/0/token/DoDetails"
  const API_Delete = API_WEB_URLS.MASTER + "/0/token/DeleteContract"
  const API_SaveCommodity = `${API_WEB_URLS.ItemMaster}/0/token`

 

  useEffect(() => {
    // Load master data
    Fn_FillListData(dispatch, setState, "PartyAccountArray", API_URL + "/Id/0")
    Fn_FillListData(dispatch, setState, "BrokerAccountArray", API_URLBroker + "/Id/0")
    Fn_FillListData(dispatch, setState, "TaxAccountArray", API_URL1 + "/Id/0")
    Fn_FillListData(dispatch, setState, "UnitArray", API_URL2 + "/Id/0")
    Fn_FillListData(dispatch, setState, "ItemArray", API_URL3 + "/Id/0")
    Fn_FillListData(dispatch, setState, "MonthArray", API_URL4 + "/Id/0")
    Fn_FillListData(dispatch, setState, "YearArray", API_URL4Year + "/Id/0")
    // Load GlobalOptions from localStorage instead of API
    const globalOptions = getGlobalOptions();
    setState(prevState => ({ ...prevState, GlobalArray: globalOptions }));
    Fn_FillListData(dispatch, setState, "ContractHNo", API_URL7 + "/Id/0")
    Fn_FillListData(dispatch, setState, "NewArray", API_URL9 + "/Id/0")

    // Reset lifting data and calculations on initial load
    setLiftingRows(createDefaultLiftingRows(7))
    setState(prev => ({
      ...prev,
      LiftingArray: [],
      totalLiftingQty: 0,
      DiffAmt: "0.00",
      showLiftingAlert: false,
      liftingAlertMessage: "",
      isEditMode: false,
      isFieldsDisabled: false,
    }))
  }, [])

  // Set ContractNo to ContractNoNew when NewArray is loaded (for new contracts)
  useEffect(() => {
    if (
      state.NewArray &&
      state.NewArray.length > 0 &&
      state.NewArray[0]?.ContractNoNew
    ) {
      setState(prev => ({
        ...prev,
        ContractNo: state.NewArray[0].ContractNoNew,
      }))
    }
  }, [state.NewArray])

  // Focus on ContractNo field when page loads
  useEffect(() => {
    // Focus on ContractNo field when page loads
    if (contractNoRef.current) {
      contractNoRef.current.focus()
    }
  }, [])

  // Check for contract data passed from navigation state (e.g., from LedgerReport)
  useEffect(() => {
    if (location.state?.Id) {
      const contractId = location.state.Id
      console.log("Loading contract from navigation state:", contractId)

      // Set the contract number and trigger loading
      setState(prev => ({
        ...prev,
        ContractNo: contractId,
      }))

      // Load the contract data
      loadContractByContractNo(contractId)
    }
  }, [location.state])

  // Check for contract data passed as modal prop
  useEffect(() => {
    if (isModal && contractData) {
      console.log("Loading contract from modal props:", contractData)

      // Try to load by ID first, then by ContractNo as fallback
      let contractIdentifier =
        contractData.Id || contractData.ContractId || contractData.Contract_Id

      if (!contractIdentifier && contractData.ContractNo) {
        // If no ID found, try to load by ContractNo
        contractIdentifier = contractData.ContractNo
      }

      if (contractIdentifier) {
        // Set the contract number and trigger loading
        setState(prev => ({
          ...prev,
          ContractNo: contractIdentifier,
        }))

        // Load the contract data
        loadContractByContractNo(contractIdentifier)
      } else {
        console.error(
          "No contract identifier found in contract data:",
          contractData
        )
        toast.error("No contract identifier found")
      }
    }
  }, [isModal, contractData])

  // Function to load contract by contract number
  const loadContractByContractNo = async contractId => {
    try {
      await Fn_FillListData(
        dispatch,
        setState,
        "ContractArray",
        `${API_URL8}/Id/${contractId}`
      )

      // After ContractArray is updated, check if we found a contract
      setTimeout(async () => {
        setState(prev => {
          const contract = prev.ContractArray[0] || {}

          // If we found a contract, load its data for editing
          if (contract.Id) {
            // Load contact person data for seller and buyer
            if (contract.F_PartyLedger) {
              Fn_FillListData(
                dispatch,
                setState,
                "PartyAccountArray1",
                `${API_URL6}/Id/${contract.F_PartyLedger}`
              )
            }

            if (contract.F_BrokerLedger) {
              Fn_FillListData(
                dispatch,
                setState,
                "BrokerAccountArray2",
                `${API_URL6}/Id/${contract.F_BrokerLedger}`
              )
            }

            // Fetch lifting data using ContractH ID
            Fn_FillListData(
              dispatch,
              setState,
              "LiftingArray",
              `${API_URL10}/Id/${contract.Id}`
            )

            // Show success message
            toast.success(`Contract ${contractId} loaded successfully!`)

            return {
              ...prev,
              id: contract.Id || 0,
              Date:
                formatDateForInput(contract.Date) ||
                new Date().toISOString().split("T")[0],
              ContractNo: contract.ContractNo || contractId,
              BroContNo: contract.BroContNo || "",
              BroContDate: contract.BroContDate || "",
              F_PartyLedger: contract.F_PartyLedger || 0,
              F_BrokerLedger: contract.F_BrokerLedger || 0,
              F_ItemType: contract.F_ItemType || 0,
              Qty: contract.Qty || 0,
              Rate: contract.Rate || 0,
              InvRate: contract.InvRate || contract.Rate || 0,
              F_UnitMaster: contract.F_UnitMaster || 0,
              F_MonthMaster: contract.F_MonthMaster || 0,
              F_YearMaster: contract.F_YearMaster || 0,
              Vessel: contract.Vessel || "",
              DeliveryPort: contract.DeliveryPort || "",
              ShipMentFromDate: contract.ShipMentFromDate || "",
              ShipMentToDate: contract.ShipMentToDate || "",
              LiftedFromDate: contract.LiftedFromDate || "",
              LiftedToDate: contract.LiftedToDate || "",
              F_ContractLedger: contract.F_ContractLedger || 0,
              Remarks: contract.Remarks || "",
              AdvPayment: contract.AdvPayment || "",
              AdvDate: contract.AdvDate || "",
              Note5: contract.Note5 || "",
              Note6: contract.Note6 || "",
                Note1: contract.Note1 || "",
              Note2: contract.Note2 || "",
              Note3: contract.Note3 || "",
              Note4: contract.Note4 || "",
              
              importDuty: contract.importDuty || 0,
              tariff: contract.tariff || 0,
              exchangeRate: contract.exchangeRate || 0,
              calPerTonDuty: contract.calPerTonDuty || 0,
              isEditMode: true,
              isFieldsDisabled: true,
              contractQty: contract.Qty || 0,
              BackSellerCNo: contract.BackSellerCNo || "",
              LiftingLedger: contract.LiftingLedger || 0,
              Type: contract.Type || 1,
            }
          } else {
            // No existing contract found
            console.log("No contract found with contract ID:", contractId)
            toast.error(`No contract found with contract ID: ${contractId}`)
            return {
              ...prev,
              id: 0,
              ContractArray: [],
              LiftingArray: [],
                      isEditMode: false,
        isFieldsDisabled: false,
        ContractNo: contractId,
            }
          }
        })
      }, 200)
    } catch (error) {
      console.error("Error loading contract:", error)
      toast.error(`Error loading contract: ${error.message}`)
      setState(prev => ({
        ...prev,
        id: 0,
        ContractArray: [],
        LiftingArray: [],
        isEditMode: false,
        isFieldsDisabled: false,
        ContractNo: contractId,
      }))
    }
  }

  // Process LiftingArray data when it changes
  useEffect(() => {
    // Only process if we're in edit mode (contract is selected) or if LiftingArray has actual data
    if (
      !state.isEditMode &&
      (!state.LiftingArray || state.LiftingArray.length === 0)
    ) {
      // Reset to empty state on initial load
      setLiftingRows([
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
        { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
      ])
      setState(prev => ({
        ...prev,
        totalLiftingQty: 0,
        DiffAmt: "0.00",
        showLiftingAlert: false,
        liftingAlertMessage: "",
      }))
      return
    }

    console.log("LiftingArray changed:", state.LiftingArray)

    let totalLiftingQty = 0
    let liftingRowsData = []

    if (state.LiftingArray && state.LiftingArray.length > 0) {
      // Process all lifting records from LiftingArray
      state.LiftingArray.forEach((record, index) => {
        console.log(`Processing lifting record ${index}:`, record)
        if (
          record.Date1 ||
          record.LorryNo ||
          record.BNo ||
          record.Lifted ||
          record.Rate1 ||
          record.DiffAmt ||
          record.AvgRate
        ) {
          const liftingRowData = {
            Date1: formatDateForInput(record.Date1 || ""),
            LorryNo: record.LorryNo || "",
            BNo: record.BNo || "",
            Lifted: record.Lifted || "",
            Rate1: record.Rate1 || "",
            DiffAmt: record.DiffAmt || "",
            AvgRate: record.AvgRate || "",
          }

          liftingRowsData.push(liftingRowData)
          totalLiftingQty += parseFloat(record.Lifted || 0)
          console.log(`Added lifting row:`, liftingRowData)
        }
      })

      console.log("Total lifting rows found:", liftingRowsData.length)
      console.log("Total lifting quantity:", totalLiftingQty)
    }

    // Set lifting rows based on the processed data
    if (liftingRowsData.length > 0) {
      setLiftingRows(liftingRowsData)
    } else {
      setLiftingRows(createDefaultLiftingRows(7))
    }

    // Only calculate DiffAmt and show alerts if we have a contract selected
    if (state.isEditMode && (state.Qty > 0 || state.contractQty > 0)) {
      // Calculate DiffAmt
      const invoiceRate = parseFloat(state.InvRate || state.Rate || 0)
      let totalDiffAmt = 0

      liftingRowsData.forEach(row => {
        const liftedQty = parseFloat(row.Lifted || 0)
        const liftingRate = parseFloat(row.Rate1 || 0)
        if (liftedQty > 0 && liftingRate > 0) {
          // Calculate difference for this row: (lifting rate - invoice rate) * quantity
          const rowDiffAmt = (liftingRate - invoiceRate) * liftedQty
          totalDiffAmt += rowDiffAmt
        }
      })

      const diffAmt = totalDiffAmt
      console.log("diffAmt", diffAmt)

      // Update lifting validation state
      setState(prev => ({
        ...prev,
        totalLiftingQty,
        DiffAmt: diffAmt.toFixed(2),
        showLiftingAlert: totalLiftingQty > (prev.contractQty || prev.Qty || 0),
        liftingAlertMessage:
          totalLiftingQty > (prev.contractQty || prev.Qty || 0)
            ? `Lifting quantity (${totalLiftingQty}) exceeds contract quantity (${
                prev.contractQty || prev.Qty || 0
              }). Please adjust.`
            : "",
      }))
    } else {
      // Reset calculations when no contract is selected
      setState(prev => ({
        ...prev,
        totalLiftingQty,
        DiffAmt: "0.00",
        showLiftingAlert: false,
        liftingAlertMessage: "",
      }))
    }
  }, [state.LiftingArray, state.isEditMode, state.Qty, state.contractQty])

  // Function to console log contract details
  const handleFillDoDetails = async () => {
    const DoDetails = {
      Id: state.id,
      ContractNo: state.ContractNo,
      Date: state.Date,
      Party: state.PartyAccountArray.find(p => p.Id === state.F_PartyLedger)?.Name || "Not Selected",
      Broker: state.BrokerAccountArray.find(b => b.Id === state.F_BrokerLedger)?.Name || "Not Selected", 
      ContractType : state.TaxAccountArray.find(c => c.Id === state.F_ContractLedger)?.Name || "Not Selected",
      Commodity: state.ItemArray.find(i => i.Id === state.F_ItemType)?.Name || "Not Selected",
      Quantity: state.Qty,
      Unit: state.UnitArray.find(u => u.Id === state.F_UnitMaster)?.Name || "Not Selected",
      Vessel: state.Vessel,
      DeliveryPort: state.DeliveryPort,
    };

    // Set DoDetails in state
    setState(prev => ({ ...prev, DoDetails: DoDetails }));
    
    // Console log to confirm it's been saved
    console.log("DoDetails saved to state:", DoDetails);

    // First fetch existing data from API
    let NewDoDetailsArray = await Fn_FillListData(dispatch, setState, "DoDetailsArray", `${API_URL12}/Id/${state.id}`)
    console.log("DoDetailsArray:", NewDoDetailsArray);

    // If no existing data or empty array, add one empty row
    if (!NewDoDetailsArray || NewDoDetailsArray.length === 0) {
      const obj = {
        DoNo : '',
        DoDate : '',
        Qty : '',
        TankNo : '',
        TransName : '',
        LogisticsFirm : '',
      }
      NewDoDetailsArray = [obj]; // Only one row
    }
    
    // Update state with array (either existing data or one new row)
    setState(prev => ({ ...prev, DoDetailsArray: NewDoDetailsArray }));

    // Wait a moment for state to update, then open modal
    setTimeout(() => {
      setDoDetailsModal(true);
      // Focus on new row's DoNo field after modal opens
      setTimeout(() => {
        if (doNoInputRef.current) {
          doNoInputRef.current.focus();
        }
      }, 200);
    }, 100);
  }

  // Handle adding new row to DoDetailsArray
  const handleAddNewDoDetailsRow = () => {
    const newRow = {
      DoNo: '',
      DoDate: '',
      Qty: '',
      TankNo: '',
      TransName: '',
      LogisticsFirm: '',
    };
    
    setState(prev => ({
      ...prev,
      DoDetailsArray: [...prev.DoDetailsArray, newRow]
    }));
  }

  // Handle DoDetails table input changes
  const handleDoDetailsInputChange = (index, field, value) => {
    setState(prev => {
      const updatedArray = [...prev.DoDetailsArray];
      
      // If quantity is being changed, validate total doesn't exceed contract quantity
      if (field === "Qty") {
        const newQty = parseFloat(value) || 0;
        const contractQty = parseFloat(prev.DoDetails.Quantity || prev.Qty || 0);
        
        // Calculate total quantity of all rows (excluding current row being edited)
        let totalQty = 0;
        updatedArray.forEach((row, idx) => {
          if (idx !== index) {
            totalQty += parseFloat(row.Qty || 0);
          }
        });
        
        // Add the new quantity being entered
        totalQty += newQty;
        
        // If total exceeds contract quantity, don't update and show error
        if (totalQty > contractQty) {
          alert(`Total quantity (${totalQty}) cannot exceed Contract quantity (${contractQty}).`);
          return prev; // Don't update state
        }
      }
      
      updatedArray[index] = { ...updatedArray[index], [field]: value };
      return { ...prev, DoDetailsArray: updatedArray };
    });
  }

  // Handle edit button click
  const handleEditDoDetails = (index) => {
    setEditingRowIndex(index);
  }

  // Single function to handle both save and update
  const handleSaveUpdateDoDetails = async (index) => {
    const rowData = state.DoDetailsArray[index];
    const isNewRow = !rowData.Id; // Check if it's a new row (no ID)
    
    // Validate total quantity before saving
    const contractQty = parseFloat(state.DoDetails.Quantity || state.Qty || 0);
    let totalQty = 0;
    
    state.DoDetailsArray.forEach((row) => {
      totalQty += parseFloat(row.Qty || 0);
    });
    
    // Check if total exceeds contract quantity
    if (totalQty > contractQty) {
      alert(`Total quantity (${totalQty}) cannot exceed Contract quantity (${contractQty}). Please adjust the quantities.`);
      return; // Don't proceed with save/update
    }
    
    // Prepare data for API call
    const apiData = {
      ...rowData,
      Id: isNewRow ? 0 : rowData.Id, // 0 for new row, existing ID for update
      F_ContractH: state.id, // Add contract reference
      DoDate: convertDateToSQLServerFormat(rowData.DoDate) // Convert date to SQL Server format (YYYY-MM-DD)
    };
    
    console.log(isNewRow ? "Saving new row:" : "Updating row:", apiData);
    
    // Create FormData for API call
    const vFormData = new FormData();
    vFormData.append("Id", apiData.Id || 0);
    vFormData.append("DoNo", apiData.DoNo || "");
    vFormData.append("DoDate", apiData.DoDate || "");
    vFormData.append("Qty", apiData.Qty || "");
    vFormData.append("TankNo", apiData.TankNo || "");
    vFormData.append("TransName", apiData.TransName || "");
    vFormData.append("LogisticsFirm", apiData.LogisticsFirm || "");
    vFormData.append("F_ContractH", state.id);

    try {
      const res = await Fn_AddEditData(
        dispatch,
        setState,
        { arguList: { id: 0, formData: vFormData } },
        'AddDoDetails/0/token',
        true,
        "AddDoDetails",
        navigate,
        "#"
      );

      console.log("API Response:", res);
      
      // Update state after successful save/update
      setState(prev => {
        const updatedArray = [...prev.DoDetailsArray];
        if (isNewRow) {
          // For new row, assign a temporary ID after save
          updatedArray[index] = { ...updatedArray[index], Id: Date.now() };
        } else {
          // For existing row, just update the data
          updatedArray[index] = { ...rowData };
        }
        return { ...prev, DoDetailsArray: updatedArray };
      });
      
      setEditingRowIndex(null);
      
      // Focus back to DoNo field after save/update
      setTimeout(() => {
        if (doNoInputRef.current) {
          doNoInputRef.current.focus();
        }
      }, 100);
      
    } catch (error) {
      console.error("Error saving/updating DoDetails:", error);
      // Optionally show error message to user
    }
  }

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingRowIndex(null);
  }

  // Commodity Modal handlers
  const handleCommodityModalInputChange = (field, value) => {
    setNewCommodityData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const toggleCommodityModal = () => {
    setCommodityModal(!commodityModal)
    if (!commodityModal) {
      setNewCommodityData({ Name: "", Alias: "" })
    }
  }

  // Save new commodity data
  const handleSaveNewCommodity = async () => {
    const formData = new FormData()
    formData.append("Name", newCommodityData.Name || "")
    formData.append("Alias", newCommodityData.Alias || "")
     

    const res = await Fn_AddEditData(
      dispatch,
      setNewCommodityData,
      { arguList: { id: 0, formData } },
      API_SaveCommodity,
      true,
      "NewTender",
      navigate,
      "#"
    )

    setCommodityModal(false)
    Fn_FillListData(dispatch, setState, "ItemArray", API_URL3 + "/Id/0")
  }

  // Handle print button for individual row
  const handlePrintDoDetails = (index) => {
    const rowData = state.DoDetailsArray[index];
    const contractDetails = state.DoDetails;
    
    // Dynamic labels based on Type (1=Buy, 2=Sell)
    const isBuyType = state.Type === 1;
    const partyLabel = isBuyType ? 'Seller' : 'Buyer';
    const partyPlaceholder = isBuyType ? 'SELLER NAME' : 'BUYER NAME';
    
    // Create print content matching the exact format shown
    const printContent = `
      <html>
        <head>
          <title>Delivery Order</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 30px; 
              font-size: 12px;
              line-height: 1.4;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
              margin-bottom: 20px;
            }
            .company-name {
              font-size: 18px;
              font-weight: bold;
              margin: 0;
            }
            .company-address {
              font-size: 12px;
              margin: 5px 0;
            }
            .ref-date {
              display: flex;
              justify-content: space-between;
              margin: 15px 0;
              border-bottom: 1px solid #000;
              padding-bottom: 5px;
            }
            .to-section {
              margin: 20px 0;
            }
            .delivery-text {
              margin: 25px 0;
              border-bottom: 1px solid #000;
              padding-bottom: 5px;
            }
            .details-grid {
              margin: 20px 0;
            }
            .detail-row {
              display: flex;
              margin: 8px 0;
              min-height: 20px;
            }
            .detail-label {
              width: 120px;
              font-weight: bold;
            }
            .detail-value {
              border-bottom: 1px solid #000;
              flex: 1;
              padding-bottom: 2px;
              margin-right: 100px;
            }
            .special-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin: 8px 0;
            }
            .vessel-box {
              border: 2px solid #000;
              padding: 5px 10px;
              font-weight: bold;
            }
            .igst-box {
              border: 1px solid #000;
              padding: 3px 8px;
              font-size: 10px;
            }
            .tank-info {
              font-weight: bold;
            }
            .commodity-section {
              margin: 15px 0;
            }
            .port-info {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .broker-info {
              font-size: 10px;
            }
            .notice-box {
              background-color: #333;
              color: white;
              text-align: center;
              padding: 5px;
              margin: 30px 0 20px 0;
              font-size: 10px;
            }
            .footer {
              display: flex;
              justify-content: space-between;
              margin-top: 30px;
            }
            .signature-section {
              width: 45%;
            }
            .office-address {
              width: 45%;
              font-size: 10px;
            }
            .office-title {
              font-weight: bold;
              margin-bottom: 5px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">MANTORA OIL PRODUCTS PVT. LTD.</div>
            <div class="company-address">26/49, Birhana Road, Kanpur – 208 001.</div>
          </div>

          <div class="ref-date">
            <div><strong>Ref No.</strong> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ${rowData.DoNo || 'S-XXX'}</div>
            <div>${new Date().toLocaleDateString('en-GB')}</div>
          </div>

          <div class="to-section">
            <strong>TO</strong><br>
            ${rowData.LogisticsFirm || 'LOGISTICS FIRM'}<br>
            ${contractDetails.DeliveryPort || 'LOCATION'}
          </div>

          <div class="delivery-text">
            <strong>Please deliver our Quantity the details are given below:</strong>
          </div>

          <div class="details-grid">
            <div class="special-row">
              <div style="display: flex; align-items: center;">
                <span class="detail-label">Importer</span>
                <span style="border-bottom: 1px solid #000; padding-bottom: 2px; width: 300px;">MANTORA OIL PRODUCTS PVT. LTD.</span>
              </div>
              <div class="igst-box">${contractDetails.ContractType || 'CONTRACT TYPE'}</div>
            </div>

            <div class="detail-row">
              <span class="detail-label">${partyLabel}</span>
              <span class="detail-value">${contractDetails.Party || partyPlaceholder}</span>
            </div>

            <div class="detail-row">
              <span class="detail-label">Vessel</span>
              <span class="vessel-box">${contractDetails.Vessel || 'ANY VESSEL'}</span>
            </div>

            <div class="special-row">
              <div style="display: flex; align-items: center;">
                <span class="detail-label">Quantity</span>
                <span style="border-bottom: 1px solid #000; padding-bottom: 2px; width: 100px;">${rowData.Qty || '0.000'}</span>
              </div>
              <div class="tank-info">TANK NO. ${rowData.TankNo || 'XXX'}</div>
            </div>

            <div class="commodity-section">
              <div class="detail-row">
                <span class="detail-label">Commodity</span>
                <span class="detail-value">${contractDetails.Commodity || 'COMMODITY NAME'}</span>
              </div>
            </div>

            <div class="port-info">
              <div style="display: flex; align-items: center;">
                <span class="detail-label">Port</span>
                <span style="border-bottom: 1px solid #000; padding-bottom: 2px; width: 150px;">${contractDetails.DeliveryPort || 'PORT NAME'}</span>
              </div>
              <div class="broker-info">
                BAR DT. &nbsp;&nbsp; ${rowData.DoDate || new Date().toLocaleDateString('en-GB')}<br>
                BROKER &nbsp;&nbsp; ${contractDetails.Broker || 'BROKER NAME'}
              </div>
            </div>

            <div class="detail-row">
              <span class="detail-label">Transporter</span>
              <span class="detail-value">AS PER BUYER INSTRUCTION / ${rowData.TransName || 'TRANSPORT NAME'}</span>
            </div>
          </div>

          <div class="notice-box">
            IMPORTANT NOTICE: BEFORE LIFTING SAMPLE DRAWN BECAUSE OUT OF TERMINAL<br>
            CARGO QUALITY COMPLAINT NOT ACCEPTED.
          </div>

          <div style="margin: 20px 0;">
            <strong>Kindly do the needful & oblige us.</strong>
          </div>

          <div class="footer">
            <div class="signature-section">
              <div>Thanking you</div>
              <div style="margin-top: 20px;">
                <div>Yours truly,</div>
                <div><strong>For Mantora Oil Products Pvt, Ltd</strong></div>
              </div>
            </div>
            <div class="office-address">
              <div class="office-title">OUR GANDHIDHAM OFFICE ADDRESS</div>
              <div>PLEASE COLLECT IGST INVOICE AT -</div>
              <div>SHOP NO.51, SILVER ARC BUILDING</div>
              <div>SECTOR - 8, GANDHIDHAM, KUTCH</div>
              <div>MR. NAKUL - 9979589927</div>
            </div>
          </div>
        </body>
      </html>
    `;
    
    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
    
    console.log("Printing DO Details for row:", rowData);
  }

  const handleInputChange = async e => {
    const { name, value, type, checked } = e.target

    // For checkboxes
    const newValue = type === "checkbox" ? checked : value

    setState(prev => ({ ...prev, [name]: newValue }))

    // Auto-update InvRate when Rate is changed
    if (name === "Rate") {
      setState(prev => ({ ...prev, InvRate: value || 0 }))

      // Recalculate DiffAmt when contract rate changes
      const invoiceRate = parseFloat(value || 0)
      let totalDiffAmt = 0

      liftingRows.forEach(row => {
        const liftedQty = parseFloat(row.Lifted || 0)
        const liftingRate = parseFloat(row.Rate1 || 0)
        if (liftedQty > 0 && liftingRate > 0) {
          // Calculate difference for this row: (lifting rate - invoice rate) * quantity
          const rowDiffAmt = (liftingRate - invoiceRate) * liftedQty
          totalDiffAmt += rowDiffAmt
        }
      })

      const diffAmt = totalDiffAmt

      setState(prev => ({
        ...prev,
        InvRate: value || 0,
        DiffAmt: diffAmt.toFixed(2),
      }))
    }

    // Recalculate lifting quantities when contract quantity changes
    if (name === "Qty") {
      const contractQty = parseFloat(value || 0)
      const totalLiftingQty = liftingRows.reduce((sum, row) => {
        return sum + parseFloat(row.Lifted || 0)
      }, 0)

      setState(prev => ({
        ...prev,
        contractQty,
        totalLiftingQty,
        showLiftingAlert: totalLiftingQty > contractQty,
        liftingAlertMessage:
          totalLiftingQty > contractQty
            ? `Lifting quantity (${totalLiftingQty}) exceeds contract quantity (${contractQty}). Please adjust.`
            : "",
      }))
    }

    if (name === "F_PartyLedger" && value) {
      // Call API with selected ID for PartyAccountArray1
      await Fn_FillListData(
        dispatch,
        setState,
        "PartyAccountArray1",
        `${API_URL6}/Id/${value}`
      )
    }

    if (name === "F_BrokerLedger" && value) {
      // Call API with selected ID for TaxAccountArray1
      await Fn_FillListData(
        dispatch,
        setState,
        "PartyAccountArray2",
        `${API_URL6}/Id/${value}`
      )
    }

    if (name === "ContractNo" && value) {
      // Check if this is an existing contract by trying to load it
      try {
        await Fn_FillListData(
          dispatch,
          setState,
          "ContractArray",
          `${API_URL8}/Id/${value}`
        )

        // After ContractArray is updated, check if we found a contract
        setTimeout(async () => {
          setState(prev => {
            const contract = prev.ContractArray[0] || {}

            // If we found a contract, load its data for editing
            if (contract.Id) {
              // Load contact person data for seller and buyer
              if (contract.F_PartyLedger) {
                Fn_FillListData(
                  dispatch,
                  setState,
                  "PartyAccountArray1",
                  `${API_URL6}/Id/${contract.F_PartyLedger}`
                )
              }

              if (contract.F_BrokerLedger) {
                Fn_FillListData(
                  dispatch,
                  setState,
                  "PartyAccountArray2",
                  `${API_URL6}/Id/${contract.F_BrokerLedger}`
                )
              }

              // Fetch lifting data using ContractH ID
              Fn_FillListData(
                dispatch,
                setState,
                "LiftingArray",
                `${API_URL10}/Id/${contract.Id}`
              )

              return {
                ...prev,
                id: contract.Id || 0,
                Date:
                  formatDateForInput(contract.Date) ||
                  new Date().toISOString().split("T")[0],
                ContractNo: contract.ContractNo || value,
                F_PartyLedger: contract.F_PartyLedger || 0,
                F_BrokerLedger: contract.F_BrokerLedger || 0,
                F_ItemType: contract.F_ItemType || 0,
                Qty: contract.Qty || 0,
                Rate: contract.Rate || 0,
                InvRate: contract.InvRate || contract.Rate || 0,
                F_UnitMaster: contract.F_UnitMaster || 0,
                F_MonthMaster: contract.F_MonthMaster || 0,
                F_YearMaster: contract.F_YearMaster || 0,
                Vessel: contract.Vessel || "",
                DeliveryPort: contract.DeliveryPort || "",
                ShipMentFromDate: contract.ShipMentFromDate || "",
                ShipMentToDate: contract.ShipMentToDate || "",
                LiftedFromDate: contract.LiftedFromDate || "",
                LiftedToDate: contract.LiftedToDate || "",
                F_ContractLedger: contract.F_ContractLedger || 0,
                Remarks: contract.Remarks || "",
                AdvPayment: contract.AdvPayment || "",
                AdvDate: contract.AdvDate || "",
                Note5: contract.Note5 || "",
                Note1: contract.Note1 || "",
                Note2: contract.Note2 || "",
                Note3: contract.Note3 || "",
                Note4: contract.Note4 || "",
                Note6: contract.Note6 || "",
                importDuty: contract.importDuty || 0,
                tariff: contract.tariff || 0,
                exchangeRate: contract.exchangeRate || 0,
                calPerTonDuty: contract.calPerTonDuty || 0,
                isEditMode: true,
                isFieldsDisabled: true,
                contractQty: contract.Qty || 0,
                BackSellerCNo: contract.BackSellerCNo || "",
                PartyPerson: contract.PartyPerson || "",
                BrokerPerson: contract.BrokerPerson || "",
                LiftingLedger: contract.LiftingLedger || 0,
                Type: contract.Type || 1,
              }
            } else {
              // No existing contract found, this is a new contract number
              return {
                ...prev,
                id: 0,
                ContractArray: [],
                LiftingArray: [],
                        isEditMode: false,
        isFieldsDisabled: false,
        // Keep the manually entered contract number
        ContractNo: value,
              }
            }
          })
        }, 200)
      } catch (error) {
        // If API call fails, treat as new contract
        console.log("No existing contract found, treating as new contract")
        setState(prev => ({
          ...prev,
          id: 0,
          ContractArray: [],
          LiftingArray: [],
                  isEditMode: false,
        isFieldsDisabled: false,
        ContractNo: value,
        }))
      }
    } else if (name === "ContractNo" && !value) {
      // Clear all data when no contract is selected
      setLiftingRows(createDefaultLiftingRows(7))
      setState(prev => ({
        ...prev,
        ContractArray: [],
        LiftingArray: [],
        totalLiftingQty: 0,
        showLiftingAlert: false,
        liftingAlertMessage: "",
        isEditMode: false,
        isFieldsDisabled: true,
        // Reset ContractNo to ContractNoNew for new contracts
        ContractNo: state.NewArray?.[0]?.ContractNoNew || "",
      }))
    }
  }

   

  // Handle modal form input changes
  const handleModalInputChange = (field, value) => {
    setNewPartyData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSave = async () => {
    try {
      setState(prev => ({
        ...prev,
        isProgress: true,
        showSuccessMessage: false,
        showErrorMessage: false,
      }))

      const formData = new FormData()

      // Use current state values instead of ContractArray values for editing
      formData.append("Date", state.Date || "")
      formData.append("Type", state.Type || 1) // 1 for Buy, 2 for Sell
      formData.append(
        "ContractNo",
        state.ContractNo ||
          state.NewArray?.[0]?.ContractNoNew ||
          state.ContractArray?.[0]?.ContractNo ||
          ""
      )
      formData.append("BroContNo", state.BroContNo || "")
      formData.append("BroContDate", state.BroContDate || "")
      formData.append("F_UnitMaster", state.F_UnitMaster || 0)
      formData.append("F_PartyLedger", state.F_PartyLedger || 0)
      formData.append("F_BrokerLedger", state.F_BrokerLedger || 0)
      formData.append("F_ItemType", state.F_ItemType || 0)
      formData.append("Qty", state.Qty || 0)
      formData.append("Rate", state.Rate || 0)
      formData.append("InvRate", state.InvRate || state.Rate || 0)
      formData.append("F_MonthMaster", state.F_MonthMaster || 0)
      formData.append("F_YearMaster", state.F_YearMaster || 0)
      formData.append("Vessel", state.Vessel || "")
      formData.append("DeliveryPort", state.DeliveryPort || "")
      formData.append("ShipMentFromDate", state.ShipMentFromDate || "")
      formData.append("ShipMentToDate", state.ShipMentToDate || "")
      formData.append("LiftedFromDate", state.LiftedFromDate || "")
      formData.append("LiftedToDate", state.LiftedToDate || "")
      formData.append("F_ContractLedger", state.F_ContractLedger || 0)
      formData.append("Remarks", state.Remarks || "")
      formData.append("AdvPayment", state.AdvPayment || "")
      formData.append("AdvDate", state.AdvDate || "")
      formData.append("BackSellerCNo", state.BackSellerCNo || "")
      formData.append("importDuty", state.importDuty || 0)
      formData.append("tariff", state.tariff || 0)
      formData.append("exchangeRate", state.exchangeRate || 0)
      formData.append("calPerTonDuty", state.calPerTonDuty || 0)
      formData.append("PartyPerson", state.PartyAccountArray1?.[0]?.Person || "")
      formData.append("BrokerPerson", state.PartyAccountArray2?.[0]?.Person || "")
      formData.append("Note5", state.Note5 || "")
      formData.append("Note6", state.Note6 || "")
      formData.append("Note1", state.Note1 || "")
      formData.append("Note2", state.Note2 || "")
      formData.append("Note3", state.Note3 || "")
      formData.append("Note4", state.Note4 || "")
      formData.append("LiftingLedger", state.LiftingLedger || 0)

      const res = await Fn_AddEditData(
        dispatch,
        setState,
        { arguList: { id: state.id || 0, formData } },
        API_URL_SAVE,
        true,
        "NewTender",
        navigate,
        "#"
      )

      console.log("res", res)

      // Get contract quantity for validation
      const contractQty = parseFloat(
        state.Qty || state.ContractArray?.[0]?.Qty || 0
      )

      // Calculate total lifting quantity
      const validLiftingRows = liftingRows.filter(
        row => row.Date1 || row.LorryNo || row.BNo || row.Lifted || row.Rate1
      )

      const totalLiftingQty = validLiftingRows.reduce((sum, row) => {
        return sum + parseFloat(row.Lifted || 0)
      }, 0)

      // Validate lifting quantity
      if (totalLiftingQty > contractQty) {
        setState(prev => ({
          ...prev,
          isProgress: false,
          showLiftingAlert: true,
          liftingAlertMessage: `Lifting quantity (${totalLiftingQty}) cannot exceed contract quantity (${contractQty}). Please adjust the lifting quantities.`,
        }))
        return
      }

      // Save lifting data with the response ID from ContractH in JSON format
      if (validLiftingRows.length > 0) {
        const liftingDataArray = validLiftingRows.map(row => {
          const invoiceRate = parseFloat(state.InvRate || state.Rate || 0)
          const liftingRate = parseFloat(row.Rate1 || 0)
          const liftingQty = parseFloat(row.Lifted || 0)
          // Calculate difference: (lifting rate - invoice rate) * quantity
          const diffAmt = (liftingRate - invoiceRate) * liftingQty

          return {
            // F_ContractH: res.id || res.Id || state.id,
            Date: row.Date1 || "",
            LorryNo: row.LorryNo || "",
            BNo: row.BNo || "",
            Lifted: parseFloat(row.Lifted || 0),
            Rate: parseFloat(row.Rate1 || 0),
            DiffAmt: parseFloat(diffAmt.toFixed(2)),
          }
        })

        // Send lifting data as JSON array
        const liftingFormData = new FormData()
        liftingFormData.append("Data", JSON.stringify(liftingDataArray))
        liftingFormData.append("F_ContractH", res.id || res.Id || state.id)

        await Fn_AddEditData(
          dispatch,
          setState,
          { arguList: { id: 0, formData: liftingFormData } },
          API_URL_Lifting,
          true,
          "NewTender",
          navigate,
          "#"
        )
      }

      setState(prev => ({
        ...prev,
        isProgress: false,
        showSuccessMessage: true,
        showLiftingAlert: false,
        liftingAlertMessage: "",
        message: state.isEditMode
          ? "Contract updated successfully!"
          : "Contract saved successfully!",
      }))

      // Auto-hide success message after 3 seconds and reload page
      setTimeout(() => {
        setState(prev => ({ ...prev, showSuccessMessage: false }))
        onClose();
        // Reload the page after successful save/update
        // window.location.reload()
      }, 1000)
    } catch (error) {
      console.error("Error saving contract:", error)
      setState(prev => ({
        ...prev,
        isProgress: false,
        showErrorMessage: true,
        message: "Error saving contract. Please try again.",
      }))

      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        setState(prev => ({ ...prev, showErrorMessage: false }))
      }, 5000)
    }
  }

  const handleDelete = () => {
    console.log("Deleting contract")

    // Get the contract ID from the selected contract
    const contractId = state.ContractArray?.[0]?.Id

    if (!contractId) {
      alert("Please select a contract first to delete")
      return
    }

    // Confirm deletion
    if (window.confirm("Are you sure you want to delete this contract?")) {
      // Call the delete function with correct parameters
      Fn_DeleteData(dispatch, setState, contractId, API_Delete, true)
        .then(response => {
          console.log("Delete successful:", response)
          // After successful deletion, clear the form and refresh
          setState(prev => ({
            ...prev,
            id: 0,
            ContractArray: [],
            // Reset other form fields as needed
          }))
          // Show success message
          alert("Contract deleted successfully!")
          // Optionally navigate back or refresh the page
          navigate("/dashboard")
        })
        .catch(error => {
          console.error("Error deleting contract:", error)
          // Show error message without redirecting
          alert("Error deleting contract. Please try again.")
        })
    }
  }

  


  const handlePrint = () => {
    console.log("Printing contract")
    // Navigate to print component with contract ID
    if (state.id || state.ContractArray?.[0]?.Id) {
      const contractId = state.id || state.ContractArray[0].Id
      window.open(`/contract-print2?id=${contractId}`, '_blank')
    } else {
      // Show alert if no contract is selected
      alert("Please select a contract first to print")
    }
  }






  const handleExit = () => {
    console.log("Exiting contract form")
    if (isModal && onClose) {
      onClose()
    } else {
      navigate("/dashboard")
    }
  }

  const handleLiftingChange = (index, field, value) => {
    const updatedRows = [...liftingRows]
    updatedRows[index][field] = value
    setLiftingRows(updatedRows)

    // Calculate total lifting quantity and validate
    const contractQty = parseFloat(
      state.Qty || state.ContractArray?.[0]?.Qty || 0
    )
    const totalLiftingQty = updatedRows.reduce((sum, row) => {
      return sum + parseFloat(row.Lifted || 0)
    }, 0)

    // Calculate DiffAmt: (Rate of each row in lifting - invoice rate) * qty of that row and then sum all differences
    const invoiceRate = parseFloat(state.InvRate || state.Rate || 0)
    let totalDiffAmt = 0

    updatedRows.forEach(row => {
      const liftedQty = parseFloat(row.Lifted || 0)
      const liftingRate = parseFloat(row.Rate1 || 0)
      if (liftedQty > 0 && liftingRate > 0) {
        // Calculate difference for this row: (lifting rate - invoice rate) * quantity
        const rowDiffAmt = (liftingRate - invoiceRate) * liftedQty
        totalDiffAmt += rowDiffAmt
      }
    })

    const diffAmt = totalDiffAmt

    setState(prev => ({
      ...prev,
      totalLiftingQty,
      contractQty,
      DiffAmt: diffAmt.toFixed(2),
      showLiftingAlert: totalLiftingQty > contractQty,
      liftingAlertMessage:
        totalLiftingQty > contractQty
          ? `Lifting quantity (${totalLiftingQty}) exceeds contract quantity (${contractQty}). Please adjust.`
          : "",
    }))
  }

  const addLiftingRow = () => {
    const newRows = [
      ...liftingRows,
      { Date1: "", LorryNo: "", BNo: "", Lifted: "", Rate1: "" },
    ]
    setLiftingRows(newRows)

    // Recalculate totals and DiffAmt
    const contractQty = parseFloat(
      state.Qty || state.ContractArray?.[0]?.Qty || 0
    )
    const totalLiftingQty = newRows.reduce((sum, row) => {
      return sum + parseFloat(row.Lifted || 0)
    }, 0)

    // Calculate DiffAmt
    const invoiceRate = parseFloat(state.InvRate || state.Rate || 0)
    let totalDiffAmt = 0

    newRows.forEach(row => {
      const liftedQty = parseFloat(row.Lifted || 0)
      const liftingRate = parseFloat(row.Rate1 || 0)
      if (liftedQty > 0 && liftingRate > 0) {
        // Calculate difference for this row: (lifting rate - invoice rate) * quantity
        const rowDiffAmt = (liftingRate - invoiceRate) * liftedQty
        totalDiffAmt += rowDiffAmt
      }
    })

    const diffAmt = totalDiffAmt

    setState(prev => ({
      ...prev,
      totalLiftingQty,
      DiffAmt: diffAmt.toFixed(2),
      showLiftingAlert: totalLiftingQty > contractQty,
      liftingAlertMessage:
        totalLiftingQty > contractQty
          ? `Lifting quantity (${totalLiftingQty}) exceeds contract quantity (${contractQty}). Please adjust.`
          : "",
    }))
  }

  const removeLiftingRow = index => {
    const updatedRows = [...liftingRows]
    updatedRows.splice(index, 1)
    setLiftingRows(updatedRows)

    // Recalculate totals and DiffAmt
    const contractQty = parseFloat(
      state.Qty || state.ContractArray?.[0]?.Qty || 0
    )
    const totalLiftingQty = updatedRows.reduce((sum, row) => {
      return sum + parseFloat(row.Lifted || 0)
    }, 0)

    // Calculate DiffAmt
    const invoiceRate = parseFloat(state.InvRate || state.Rate || 0)
    let totalDiffAmt = 0

    updatedRows.forEach(row => {
      const liftedQty = parseFloat(row.Lifted || 0)
      const liftingRate = parseFloat(row.Rate1 || 0)
      if (liftedQty > 0 && liftingRate > 0) {
        // Calculate difference for this row: (lifting rate - invoice rate) * quantity
        const rowDiffAmt = (liftingRate - invoiceRate) * liftedQty
        totalDiffAmt += rowDiffAmt
      }
    })

    const diffAmt = totalDiffAmt

    setState(prev => ({
      ...prev,
      totalLiftingQty,
      DiffAmt: diffAmt.toFixed(2),
      showLiftingAlert: totalLiftingQty > contractQty,
      liftingAlertMessage:
        totalLiftingQty > contractQty
          ? `Lifting quantity (${totalLiftingQty}) exceeds contract quantity (${contractQty}). Please adjust.`
          : "",
    }))
  }

  // Handle lifting keyboard navigation
  const handleLiftingKeyDown = (e, rowIndex, fieldName) => {
    if (e.key === "Enter") {
      e.preventDefault()

      if (fieldName === "Rate1") {
        // On Rate field Enter, add new row and focus on its date
        const currentRowCount = liftingRows.length
        addLiftingRow()
        setTimeout(() => {
          // Focus on the new row's Date field (which will be at the current count index)
          const newRowDateInput = document.querySelector(
            `input[name="Date1-${currentRowCount}"]`
          )
          if (newRowDateInput) {
            newRowDateInput.focus()
          }
        }, 150)
      } else {
        // Navigate to next field in same row or next row
        const nextField = getNextLiftingField(fieldName)
        if (nextField) {
          const nextInput = document.querySelector(
            `input[name="${nextField}-${rowIndex}"]`
          )
          if (nextInput) {
            nextInput.focus()
          }
        }
      }
    }
  }

  // Get next field for lifting navigation
  const getNextLiftingField = currentField => {
    const fieldOrder = ["Date1", "LorryNo", "BNo", "Lifted", "Rate1"]
    const currentIndex = fieldOrder.indexOf(currentField)
    return currentIndex < fieldOrder.length - 1
      ? fieldOrder[currentIndex + 1]
      : null
  }

  // Wrapper functions for lifting operations
  const handleAddLiftingRow = () => {
    addLiftingRow()
  }

  const handleRemoveLiftingRow = index => {
    removeLiftingRow(index)
  }

  // Handle Enter key navigation with conditional logic
  const handleKeyDown = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault()

      const fieldName = e.target.name

      // Check if shipment month is selected
      const isShipmentMonthSelected =
        state.F_MonthMaster && state.F_MonthMaster !== 0 && state.F_YearMaster && state.F_YearMaster !== 0
      // Check if shipment dates are selected - check for both truthy values and non-empty strings
      const areShipmentDatesSelected =
        state.ShipMentFromDate &&
        state.ShipMentToDate &&
        state.ShipMentFromDate !== "" &&
        state.ShipMentToDate !== "" &&
        state.ShipMentFromDate !== null &&
        state.ShipMentToDate !== null
      // Check if lifting dates are selected - check for both truthy values and non-empty strings
      const areLiftingDatesSelected =
        state.LiftedFromDate &&
        state.LiftedToDate &&
        state.LiftedFromDate !== "" &&
        state.LiftedToDate !== "" &&
        state.LiftedFromDate !== null &&
        state.LiftedToDate !== null

      // Handle delivery port navigation based on conditions
      if (fieldName === "DeliveryPort") {
        console.log("DeliveryPort navigation - Debug info:")
        console.log(
          "isShipmentMonthSelected:",
          isShipmentMonthSelected,
          "month:",
          state.F_MonthMaster,
          "year:",
          state.F_YearMaster
        )
        console.log(
          "areShipmentDatesSelected:",
          areShipmentDatesSelected,
          "values:",
          state.ShipMentFromDate,
          state.ShipMentToDate
        )
        console.log(
          "areLiftingDatesSelected:",
          areLiftingDatesSelected,
          "values:",
          state.LiftedFromDate,
          state.LiftedToDate
        )

        // Also check the actual DOM values to be sure
        const shipmentFromInput = document.querySelector(
          'input[name="ShipMentFromDate"]'
        )
        const shipmentToInput = document.querySelector(
          'input[name="ShipMentToDate"]'
        )
        const liftingFromInput = document.querySelector(
          'input[name="LiftedFromDate"]'
        )
        const liftingToInput = document.querySelector(
          'input[name="LiftedToDate"]'
        )

        const domShipmentDatesSelected =
          shipmentFromInput &&
          shipmentToInput &&
          shipmentFromInput.value &&
          shipmentToInput.value
        const domLiftingDatesSelected =
          liftingFromInput &&
          liftingToInput &&
          liftingFromInput.value &&
          liftingToInput.value

        console.log(
          "DOM values - shipment:",
          domShipmentDatesSelected,
          "lifting:",
          domLiftingDatesSelected
        )

        // If any of the conditions are met, skip to payment terms
        if (
          isShipmentMonthSelected ||
          areShipmentDatesSelected ||
          areLiftingDatesSelected ||
          domShipmentDatesSelected ||
          domLiftingDatesSelected
        ) {
          console.log("Condition met - skipping to payment terms")
          const paymentTermsInput = document.querySelector(
            'input[name="Remarks"]'
          )
          if (paymentTermsInput) {
            paymentTermsInput.focus()
            return
          }
        } else {
          console.log(
            "No condition met - normal navigation to shipment from date"
          )
          // Normal navigation to shipment from date
          if (shipmentFromRef.current) {
            shipmentFromRef.current.focus()
            return
          }
        }
      }

      // Handle shipment from date navigation
      if (fieldName === "ShipMentFromDate") {
        if (shipmentToRef.current) {
          shipmentToRef.current.focus()
          return
        }
      }

      // Handle shipment to date navigation
      if (fieldName === "ShipMentToDate") {
        if (remarksRef.current) {
          remarksRef.current.focus()
          return
        }
      }

      // Handle lifting from date navigation
      if (fieldName === "LiftedFromDate") {
        if (liftedToRef.current) {
          liftedToRef.current.focus()
          return
        }
      }

      // Handle lifting to date navigation
      if (fieldName === "LiftedToDate") {
        if (remarksRef.current) {
          remarksRef.current.focus()
          return
        }
      }

      // Normal navigation for other fields
      if (nextRef && nextRef.current) {
        nextRef.current.focus()
      }
    }
  }

  // Helper function to get display value for form fields
  const getFieldValue = fieldName => {
    if (fieldName === "ContractNo") {
      // When adding new contract (ContractArray is empty), show ContractNoNew
      // When editing existing contract, show ContractNo
      if (state.ContractArray && state.ContractArray.length > 0) {
        return state.ContractArray[0]?.ContractNo || ""
      } else {
        return state.ContractNo || state.NewArray?.[0]?.ContractNoNew || ""
      }
    }
    return state[fieldName] || ""
  }

  return (
    <div className={`contract-page ${isModal ? "contract-modal" : ""}`} style={{ backgroundColor: '#fffacd' }}>
      {!isModal && (
        <div
          style={{
            height: "0.1rem",
          }}
        ></div>
      )}
      <div style={{ backgroundColor: '#fffacd' }}>
        {/* Success/Error Messages */}
        {state.showSuccessMessage && (
          <Alert color="success" className="mb-2">
            {state.message}
          </Alert>
        )}
        {state.showErrorMessage && (
          <Alert color="danger" className="mb-2">
            {state.message}
          </Alert>
        )}
        {state.showLiftingAlert && (
          <Alert color="warning" className="mb-2">
            <i className="bx bx-error-circle me-2"></i>
            {state.liftingAlertMessage}
          </Alert>
        )}

        <div
          className={`contract-form ${isModal ? "contract-form-modal" : ""}`}
          style={{ backgroundColor: '#fffacd' }}
        >
          <Form>
            <Row className={`g-1 ${isModal ? "g-0" : ""}`}>
              {/* Merged Contract Details and Conditions Section */}
              <Col lg={isModal ? 8 : 8}>
                <div
                  className={`form-section ${
                    isModal ? "form-section-modal" : ""
                  }`}
                  style={{ backgroundColor: '#fffacd' }}
                >
                  <h6
                    className={`section-title ${
                      isModal ? "section-title-modal" : ""
                    }`}
                    style={{
                      fontSize: isModal ? "0.9rem" : "1rem",
                      fontWeight: "bold",
                    }}
                  >
                    <i className="bx bx-file-doc me-2"></i>
                    Contract Details & Conditions{" "}
                    <span
                      style={{
                        color: state.isEditMode ? "#28a745" : "#007bff",
                        fontSize: isModal ? "0.9rem" : "1rem",
                        fontWeight: "bold",
                      }}
                    >
                      ({state.isEditMode ? "Edit Mode" : "Add Mode"})
                    </span>

                    <span
                      style={{
                        color: "#dc3545",
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                        marginLeft: "10px",
                      }}
                    >
                       ( {state.ContractHNo ? state.ContractHNo.length : 0})
                    </span>
                  </h6>

                  <Row>
                    {/* Left Side - Contract Details */}
                    <Col md={6} style={{ borderRight: "2px solid #dee2e6", paddingRight: "20px" }}>
                      

                      <Row className="g-1">
                        {/* Buy/Sell Toggle - Full Width */}
                        <Col md={12}>
                          <FormGroup className="mb-1">
                            <Label className="form-label-sm mb-1" style={{ fontSize: "0.75rem" }}>
                              Type
                            </Label>
                            <div className="btn-group w-100" role="group">
                              <button
                                type="button"
                                className={`btn btn-sm ${state.Type === 1 ? 'btn-success' : 'btn-outline-success'}`}
                                onClick={() => setState(prev => ({ ...prev, Type: 1 }))}
                                style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                              >
                                BUY
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm ${state.Type === 2 ? 'btn-danger' : 'btn-outline-danger'}`}
                                onClick={() => setState(prev => ({ ...prev, Type: 2 }))}
                                style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                              >
                                SELL
                              </button>
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-1">
                        <Col md={3}>
                          <FormGroup className="mb-1">
                            <Label
                              for="date"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Date
                            </Label>
                            <input
                              type="date"
                              name="Date"
                              id="Date"
                              value={getFieldValue("Date")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              ref={dateRef}
                              onKeyDown={e => handleKeyDown(e, contractNoSelectRef)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>

                        <Col md={3}>
                          <FormGroup className="mb-1">
                            <Label
                              for="ContractNoNew"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Serial No
                            </Label>
                            <input
                              type="text"
                              name="ContractNo"
                              id="ContractNo"
                              value={getFieldValue("ContractNo")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="Enter contract number"
                              ref={contractNoRef}
                              onKeyDown={e => handleKeyDown(e, broContNoRef)}
                              readOnly
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>

                        <Col md={3}>
                          <FormGroup className="mb-1">
                            <Label
                              for="BroContNo"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Bro Cont No
                            </Label>
                            <input
                              name="BroContNo"
                              id="BroContNo"
                              value={getFieldValue("BroContNo")}
                              onChange={handleInputChange}
                              type="text"
                              className="form-control form-control-sm py-1"
                              placeholder="Bro Cont No"
                              ref={broContNoRef}
                              onKeyDown={e => handleKeyDown(e, broContDateRef)}
                              disabled={state.isFieldsDisabled}
                              autoComplete="off"
                            />
                          </FormGroup>
                        </Col>

                        <Col md={3}>
                          <FormGroup className="mb-1">
                            <Label
                              for="BroContDate"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Bro Cont Date
                            </Label>
                            <input
                              type="date"
                              name="BroContDate"
                              id="BroContDate"
                              value={getFieldValue("BroContDate")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="Bro Cont Date"
                              ref={broContDateRef}
                              onKeyDown={e => handleKeyDown(e, partyRef)}
                              disabled={state.isFieldsDisabled}
                              autoComplete="off"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-1">
                        <Col md={7}>
                          <FormGroup className="mb-1">
                            <Label
                              for="F_PartyLedger"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {state.Type === 1 ? 'Seller' : 'Buyer'}
                            </Label>
                            <select
                              name="F_PartyLedger"
                              id="F_PartyLedger"
                              value={getFieldValue("F_PartyLedger")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              ref={partyRef}
                              onKeyDown={e => handleKeyDown(e, brokerRef)}
                              disabled={state.isFieldsDisabled}
                            >
                              <option value="">{state.Type === 1 ? 'Select Seller' : 'Select Buyer'}</option>
                              {state.PartyAccountArray.map(type => (
                                <option key={type.Id} value={type.Id}>
                                  {type.Name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>
                        

                        <Col md={5}>
                          <FormGroup className="mb-1">
                            <Label
                              for="sellerName"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Name
                            </Label>
                            <input
                              type="text"
                              name="sellerName"
                              id="Person"
                              value={state.PartyAccountArray1?.[0]?.Person || ""}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="Name"
                              readOnly
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-2">
                        <Col md={7}>
                          <FormGroup className="mb-1">
                            <Label
                              for="F_BrokerLedger"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Broker
                            </Label>
                            <select
                              name="F_BrokerLedger"
                              id="F_BrokerLedger"
                              value={getFieldValue("F_BrokerLedger")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              ref={brokerRef}
                              onKeyDown={e => handleKeyDown(e, commodityRef)}
                              disabled={state.isFieldsDisabled}
                            >
                              <option value="">Select Buyer</option>
                              {state.BrokerAccountArray.map(type => (
                                <option key={type.Id} value={type.Id}>
                                  {type.Name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>

                        

                        <Col md={5}>
                          <FormGroup className="mb-1">
                            <Label
                              for="Person"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Name
                            </Label>
                            <input
                              type="text"
                              name="Person"
                              id="Person"
                              value={state.BrokerAccountArray2?.[0]?.Person || ""}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="Name"
                              readOnly
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-2">
                        <Col md={5}>
                          <FormGroup className="mb-1">
                            <Label
                              for="Item"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Commodity
                            </Label>
                            <select
                              name="F_ItemType"
                              id="F_ItemType"
                              value={getFieldValue("F_ItemType")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              ref={commodityRef}
                              onKeyDown={e => handleKeyDown(e, qtyRef)}
                              disabled={state.isFieldsDisabled}
                            >
                              <option value="">Select Commodity</option>
                              {state.ItemArray.map(type => (
                                <option key={type.Id} value={type.Id}>
                                  {type.Name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>
                        <Col md={1}>
                          <FormGroup className="mb-1">
                            <Label
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.65rem" }}
                            >
                              &nbsp;
                            </Label>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={toggleCommodityModal}
                              disabled={state.isFieldsDisabled}
                              style={{
                                width: "100%",
                                height: "28px",
                                padding: "0",
                                fontSize: "0.8rem",
                              }}
                            >
                              +
                            </Button>
                          </FormGroup>
                        </Col>
                        <Col md={2}>
                          <FormGroup className="mb-1">
                            <Label
                              for="Qty"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Qty
                            </Label>
                            <input
                              type="number"
                              name="Qty"
                              id="Qty"
                              value={getFieldValue("Qty")}
                              onChange={handleInputChange}
                              disabled={state.isFieldsDisabled}
                              className="form-control form-control-sm py-1"
                              placeholder="0"
                              ref={qtyRef}
                              onKeyDown={e => handleKeyDown(e, unitRef)}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={2}>
                          <FormGroup className="mb-1">
                            <Label
                              for="unit"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Unit
                            </Label>
                            <select
                              name="F_UnitMaster"
                              id="F_UnitMaster"
                              value={getFieldValue("F_UnitMaster")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              ref={unitRef}
                              onKeyDown={e => handleKeyDown(e, rateRef)}
                              disabled={state.isFieldsDisabled}
                            >
                              <option value="">Select Unit</option>
                              {state.UnitArray.map(unit => (
                                <option key={unit.Id} value={unit.Id}>
                                  {unit.Name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>

                        <Col md={3}>
                          <FormGroup className="mb-1">
                            <Label
                              for="Rate"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Rate
                            </Label>
                            <input
                              type="number"
                              name="Rate"
                              id="Rate"
                              value={getFieldValue("Rate")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="0.00"
                              ref={rateRef}
                              onKeyDown={e => handleKeyDown(e, importDutyRef)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-2">
                        <Col md={3}>
                          <FormGroup className="mb-1">
                            <Label
                              for="importDuty"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Import Duty
                            </Label>
                            <input
                              type="number"
                              name="importDuty"
                              id="importDuty"
                              value={state.importDuty}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="0.00"
                              ref={importDutyRef}
                              onKeyDown={e => handleKeyDown(e, tariffRef)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={2}>
                          <FormGroup className="mb-1">
                            <Label
                              for="tariff"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Tariff
                            </Label>
                            <input
                              type="number"
                              name="tariff"
                              id="tariff"
                              value={state.tariff}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="0.00"
                              ref={tariffRef}
                              onKeyDown={e => handleKeyDown(e, exchangeRateRef)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>

                        <Col md={4}>
                          <FormGroup className="mb-1">
                            <Label
                              for="exchangeRate"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Exchange $ Rate
                            </Label>
                            <input
                              type="number"
                              name="exchangeRate"
                              id="exchangeRate"
                              value={state.exchangeRate}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="0.00"
                              ref={exchangeRateRef}
                              onKeyDown={e => handleKeyDown(e, calPerTonDutyRef)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup className="mb-1">
                            <Label
                              for="calPerTonDuty"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Duty per Ton
                            </Label>
                            <input
                              type="number"
                              name="calPerTonDuty"
                              id="calPerTonDuty"
                              value={state.calPerTonDuty}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="0.00"
                              ref={calPerTonDutyRef}
                              onKeyDown={e => handleKeyDown(e, contractTypeRef)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <FormGroup className="mb-1">
                        <Label
                          className="d-flex align-items-center form-label-sm mb-1"
                          style={{ fontSize: "0.75rem" }}
                        >
                          <i className="bx bx-calculator me-2"></i>
                          Invoice Rate:{" "}
                          <span className="text-primary fw-bold ms-2">
                            {state.InvRate || state.Rate || "0.00"}
                          </span>
                        </Label>
                      </FormGroup>
                    </Col>

                    {/* Right Side - Conditions */}
                    <Col md={6} style={{ paddingLeft: "20px" }}>
                      

                      <Row className="g-1">
                        <Col md={7}>
                          <FormGroup className="mb-1">
                            <Label
                              for="F_ContractLedger"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Contract Type
                            </Label>
                            <select
                              name="F_ContractLedger"
                              id="F_ContractLedger"
                              value={getFieldValue("F_ContractLedger") || 0}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              ref={contractTypeRef}
                              onKeyDown={e => handleKeyDown(e, shipMonthRef)}
                              disabled={state.isFieldsDisabled}
                            >
                              <option value="">Select Contract Type</option>
                              {state.TaxAccountArray.map(type => (
                                <option key={type.Id} value={type.Id}>
                                  {type.Name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>
                        

                        <Col md={3}>
                          <FormGroup className="mb-1">
                            <Label
                              for="shipMonth"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Shipment Month
                            </Label>
                            <select
                              name="F_MonthMaster"
                              id="F_MonthMaster"
                              value={getFieldValue("F_MonthMaster")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              disabled={
                                (getFieldValue("ShipMentFromDate") &&
                                  getFieldValue("ShipMentToDate")) ||
                                (getFieldValue("LiftedFromDate") &&
                                  getFieldValue("LiftedToDate"))
                              }
                              ref={shipMonthRef}
                              onKeyDown={e => handleKeyDown(e, shipYearRef)}
                            >
                              <option value="">Select Month</option>
                              {state.MonthArray.map(type => (
                                <option key={type.Id} value={type.Id}>
                                  {type.Name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>
                        <Col md={2}>
                          <FormGroup className="mb-1">
                            <Label
                              for="shipYear"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Shipment Year
                            </Label>
                            <select
                              name="F_YearMaster"
                              id="F_YearMaster"
                              value={getFieldValue("F_YearMaster")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              disabled={
                                (getFieldValue("ShipMentFromDate") &&
                                  getFieldValue("ShipMentToDate")) ||
                                (getFieldValue("LiftedFromDate") &&
                                  getFieldValue("LiftedToDate"))
                              }
                              ref={shipYearRef}
                              onKeyDown={e => handleKeyDown(e, vesselRef)}
                            >
                              <option value="">Select Year</option>
                              {state.YearArray.map(type => (
                                <option key={type.Id} value={type.Id}>
                                  {type.Name}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-2">
                        <Col md={6}>
                          <FormGroup className="mb-1">
                            <Label
                              for="Vessel"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Vessel Name
                            </Label>
                            <input
                              type="text"
                              name="Vessel"
                              id="Vessel"
                              value={getFieldValue("Vessel")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="Enter vessel name"
                              ref={vesselRef}
                              onKeyDown={e => handleKeyDown(e, deliveryPortRef)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-1">
                            <Label
                              htmlFor="DeliveryPort"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Delivery Port
                            </Label>
                            <input
                              type="text"
                              name="DeliveryPort"
                              id="DeliveryPort"
                              value={getFieldValue("DeliveryPort")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="Enter delivery port"
                              ref={deliveryPortRef}
                              onKeyDown={e => handleKeyDown(e, null)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-2">
                        <Col md={6}>
                          <FormGroup className="mb-1">
                            <Label
                              for="ShipMentFromDate"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Shipment From
                            </Label>
                            <input
                              type="date"
                              name="ShipMentFromDate"
                              id="ShipMentFromDate"
                              value={formatDateForInput(
                                getFieldValue("ShipMentFromDate")
                              )}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              disabled={
                                state.isFieldsDisabled ||
                                getFieldValue("F_MonthMaster") ||
                                (getFieldValue("LiftedFromDate") &&
                                  getFieldValue("LiftedToDate"))
                              }
                              ref={shipmentFromRef}
                              onKeyDown={e => handleKeyDown(e, null)}
                            />
                            {getFieldValue("ShipMentFromDate") && (
                              <span style={{ fontSize: "0.7rem", display: "block", marginTop: "0.25rem", color: "#007bff !important" }}>
                                {formatDateForDisplay(getFieldValue("ShipMentFromDate"))}
                              </span>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-1">
                            <Label
                              for="ShipMentToDate"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              To
                            </Label>
                            <input
                              type="date"
                              name="ShipMentToDate"
                              id="ShipMentToDate"
                              value={formatDateForInput(
                                getFieldValue("ShipMentToDate")
                              )}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              disabled={
                                state.isFieldsDisabled ||
                                getFieldValue("F_MonthMaster") ||
                                (getFieldValue("LiftedFromDate") &&
                                  getFieldValue("LiftedToDate"))
                              }
                              ref={shipmentToRef}
                              onKeyDown={e => handleKeyDown(e, null)}
                            />
                            {getFieldValue("ShipMentToDate") && (
                              <span style={{ fontSize: "0.7rem", display: "block", marginTop: "0.25rem", color: "#007bff !important" }}>
                                {formatDateForDisplay(getFieldValue("ShipMentToDate"))}
                              </span>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-2">
                        <Col md={6}>
                          <FormGroup className="mb-1">
                            <Label
                              for="LiftedFromDate"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Lifting From
                            </Label>
                            <input
                              type="date"
                              name="LiftedFromDate"
                              id="LiftedFromDate"
                              value={formatDateForInput(
                                getFieldValue("LiftedFromDate")
                              )}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              disabled={
                                state.isFieldsDisabled ||
                                getFieldValue("F_MonthMaster") ||
                                (getFieldValue("ShipMentFromDate") &&
                                  getFieldValue("ShipMentToDate"))
                              }
                              ref={liftedFromRef}
                              onKeyDown={e => handleKeyDown(e, null)}
                            />
                            {getFieldValue("LiftedFromDate") && (
                              <span style={{ fontSize: "0.7rem", display: "block", marginTop: "0.25rem", color: "#007bff !important" }}>
                                {formatDateForDisplay(getFieldValue("LiftedFromDate"))}
                              </span>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-1">
                            <Label
                              for="LiftedToDate"
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              To
                            </Label>
                            <input
                              type="date"
                              name="LiftedToDate"
                              id="LiftedToDate"
                              value={formatDateForInput(
                                getFieldValue("LiftedToDate")
                              )}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              disabled={
                                state.isFieldsDisabled ||
                                getFieldValue("F_MonthMaster") ||
                                (getFieldValue("ShipMentFromDate") &&
                                  getFieldValue("ShipMentToDate"))
                              }
                              ref={liftedToRef}
                              onKeyDown={e => handleKeyDown(e, null)}
                            />
                            {getFieldValue("LiftedToDate") && (
                              <span style={{ fontSize: "0.7rem", display: "block", marginTop: "0.25rem", color: "#007bff !important" }}>
                                {formatDateForDisplay(getFieldValue("LiftedToDate"))}
                              </span>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-2">
                        <Col md={5}>
                          <FormGroup className="mb-1">
                            <Label
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Payment Terms
                            </Label>
                            <input
                              type="text"
                              name="Remarks"
                              id="Remarks"
                              value={getFieldValue("Remarks")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="Enter payment terms"
                              ref={remarksRef}
                              onKeyDown={e => handleKeyDown(e, advPaymentRef)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup className="mb-1">
                            <Label
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Advance
                            </Label>
                            <input
                              type="text"
                              name="AdvPayment"
                              id="AdvPayment"
                              value={getFieldValue("AdvPayment")}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              placeholder="Amount in pmt"
                              ref={advPaymentRef}
                              onKeyDown={e => handleKeyDown(e, advDateRef)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>

                        <Col md={4}>
                          <FormGroup className="mb-1">
                            <Label
                              className="form-label-sm mb-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Deposit Date
                            </Label>
                            <input
                              type="date"
                              name="AdvDate"
                              id="AdvDate"
                              value={formatDateForInput(getFieldValue("AdvDate"))}
                              onChange={handleInputChange}
                              className="form-control form-control-sm py-1"
                              ref={advDateRef}
                              onKeyDown={e => handleKeyDown(e, note6Ref)}
                              disabled={state.isFieldsDisabled}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={4}>
                          <Input
                            type="text"
                            value={state.DiffAmt || "0.00"}
                            readOnly
                            className={`form-control-sm ${
                              parseFloat(state.DiffAmt || 0) > 0
                                ? "text-danger fw-bold"
                                : parseFloat(state.DiffAmt || 0) < 0
                                ? "text-success fw-bold"
                                : ""
                            }`}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  {/* Common Notes Section */}
                  <div className="notes-section mt-4" >
                    <h6
                      className="section-subtitle mb-3"
                      style={{ fontSize: "0.85rem", fontWeight: "bold", color: "#495057" }}
                    >
                      <i className="bx bx-note me-2"></i>
                      Notes
                    </h6>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <input
                            type="text"
                            name="Note6"
                            id="notes6"
                            value={state.Note6 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm mb-1"
                            placeholder="Write a Note"
                            disabled={state.isFieldsDisabled}
                            ref={note6Ref}
                            onKeyDown={e => handleKeyDown(e, note1Ref)}
                          />
                          <input
                            type="text"
                            name="notes1"
                            id="notes1"
                            value={state.Note1 || " "}
                            onChange={handleInputChange}
                            className="form-control form-control-sm mb-1"
                            placeholder="Write a Note"
                            ref={note1Ref}
                            onKeyDown={e => handleKeyDown(e, note2Ref)}
                          />
                          <input
                            type="text"
                            name="notes2"
                            id="notes2"
                            value={state.Note2 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm mb-1"
                            placeholder="Note 2"
                            ref={note2Ref}
                            onKeyDown={e => handleKeyDown(e, note3Ref)}
                          />
                        </FormGroup>
                     
                        <FormGroup>
                          <input
                            type="text"
                            id="notes3"
                            name="notes3"
                            value={state.Note3 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm mb-1"
                            placeholder="Note 3"
                            ref={note3Ref}
                            onKeyDown={e => handleKeyDown(e, note4Ref)}
                          />
                          <input
                            type="text"
                            name="notes4"
                            id="notes4"
                            value={state.Note4 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm mb-1"
                            placeholder="Note 4"
                            ref={note4Ref}
                            onKeyDown={e => handleKeyDown(e, note5Ref)}
                          />
                          <input
                            type="text"
                            name="Note5"
                            id="notes5"
                            value={state.Note5 || ""}
                            onChange={handleInputChange}
                            className="form-control form-control-sm"
                            placeholder="Write a Note"
                            ref={note5Ref}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                // Transfer focus to Save button
                                if (saveButtonRef.current) {
                                  saveButtonRef.current.focus()
                                }
                              }
                            }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>

              {/* Right Panel - Lifting Details */}
              <Col lg={isModal ? 4 : 4}>
                <div
                  className={`form-section ${
                    isModal ? "form-section-modal" : ""
                  }`}
                  style={{ backgroundColor: '#fffacd' }}
                >
                  <h6
                    className={`section-title ${
                      isModal ? "section-title-modal" : ""
                    }`}
                    style={{
                      fontSize: isModal ? "0.9rem" : "1rem",
                      fontWeight: "bold",
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "space-between"
                    }}
                  >
                    <span>
                      <i className="bx bx-truck me-2"></i>
                      Lifting Details
                    </span>
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleFillDoDetails();
                      }}
                      style={{
                        color: "red",
                        textDecoration: "none",
                        fontSize: "0.85rem",
                        fontWeight: "normal",
                        cursor: "pointer"
                      }}
                      onMouseOver={(e) => e.target.style.textDecoration = "underline"}
                      onMouseOut={(e) => e.target.style.textDecoration = "none"}
                    >
                      Fill DoDetails
                    </a>
                  </h6>

                  <span>
                     <select
                            name="LiftingLedger"
                            id="LiftingLedger"
                            value={getFieldValue("LiftingLedger")}
                            onChange={handleInputChange}
                            
                            className="form-control form-control-sm py-1"
                            style={{ width: "200px", maxWidth: "200px" , height: "30px", fontWeight: "bold"}}
                            disabled={state.isEditMode && !state.isEditingEnabled}
                          >
                            <option value="">Select Lifting</option>
                            {state.PartyAccountArray.map(type => (
                                <option key={type.Id} value={type.Id}>
                                  {type.Name}
                                </option>
                              ))}
                          </select> 
                          </span>

                  {/* Compact Lifting Table */}
                  <div className="table-container mb-2">
                    <div
                      className="table-responsive"
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: "1px solid #dee2e6",
                        borderRadius: "0.375rem",
                        boxShadow: "inset 0 0 5px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Table
                        size="sm"
                        className="table table-bordered table-sm mb-0"
                      >
                        <thead
                          className="sticky-top"
                          style={{ backgroundColor: "#87CEEB !important", color: "#000000 !important" }}
                        >
                          <tr  style={{ fontSize: "0.8rem", backgroundColor: "#87CEEB !important", color: "#000000 !important" }}>
                            <th
                              // className="py-1 px-1"
                              style={{ fontSize: "0.8rem", backgroundColor: "#87CEEB !important", color: "#000000 !important" }}
                            >
                              Date
                            </th>
                            <th
                              className="py-1 px-1"
                              style={{ fontSize: "0.8rem", backgroundColor: "#87CEEB !important", color: "#000000 !important" }}
                            >
                              Lorry
                            </th>
                            <th
                              className="py-1 px-1"
                              style={{ fontSize: "0.8rem", backgroundColor: "#87CEEB !important", color: "#000000 !important" }}
                            >
                              B.No
                            </th>
                            <th
                              className="py-1 px-1"
                              style={{ fontSize: "0.8rem", backgroundColor: "#87CEEB !important", color: "#000000 !important" }}
                            >
                              Qty
                            </th>
                            <th
                              className="py-1 px-1"
                              style={{ fontSize: "0.8rem", backgroundColor: "#87CEEB !important", color: "#000000 !important" }}
                            >
                              Rate
                            </th>
                            <th
                              className="py-1 px-1"
                              style={{ fontSize: "0.8rem", backgroundColor: "#87CEEB !important", color: "#000000 !important" }}
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {liftingRows.map((row, index) => (
                            <tr key={index}>
                              <td className="py-1 px-1">
                                <input
                                  type="date"
                                  name={`Date1-${index}`}
                                  className="form-control form-control-sm py-1"
                                  value={row.Date1 || ""}
                                  onChange={e =>
                                    handleLiftingChange(
                                      index,
                                      "Date1",
                                      e.target.value
                                    )
                                  }
                                  onKeyDown={e =>
                                    handleLiftingKeyDown(e, index, "Date1")
                                  }
                                />
                              </td>
                              <td className="py-1 px-1">
                                <input
                                  type="text"
                                  name={`LorryNo-${index}`}
                                  className="form-control form-control-sm py-1"
                                  value={row.LorryNo || ""}
                                  onChange={e =>
                                    handleLiftingChange(
                                      index,
                                      "LorryNo",
                                      e.target.value
                                    )
                                  }
                                  onKeyDown={e =>
                                    handleLiftingKeyDown(e, index, "LorryNo")
                                  }
                                  placeholder="Lorry #"
                                />
                              </td>
                              <td className="py-1 px-1">
                                <input
                                  type="text"
                                  name={`BNo-${index}`}
                                  className="form-control form-control-sm py-1"
                                  value={row.BNo || ""}
                                  onChange={e =>
                                    handleLiftingChange(
                                      index,
                                      "BNo",
                                      e.target.value
                                    )
                                  }
                                  onKeyDown={e =>
                                    handleLiftingKeyDown(e, index, "BNo")
                                  }
                                  placeholder="B.No"
                                />
                              </td>
                              <td className="py-1 px-1">
                                <input
                                  type="text"
                                  name={`Lifted-${index}`}
                                  className="form-control form-control-sm py-1"
                                  value={row.Lifted || ""}
                                  onChange={e =>
                                    handleLiftingChange(
                                      index,
                                      "Lifted",
                                      e.target.value
                                    )
                                  }
                                  onKeyDown={e =>
                                    handleLiftingKeyDown(e, index, "Lifted")
                                  }
                                  placeholder="Qty"
                                />
                              </td>
                              <td className="py-1 px-1">
                                <input
                                  type="text"
                                  name={`Rate1-${index}`}
                                  className="form-control form-control-sm py-1"
                                  value={row.Rate1 || ""}
                                  onChange={e =>
                                    handleLiftingChange(
                                      index,
                                      "Rate1",
                                      e.target.value
                                    )
                                  }
                                  onKeyDown={e =>
                                    handleLiftingKeyDown(e, index, "Rate1")
                                  }
                                  placeholder="Rate"
                                />
                              </td>
                              <td className="py-1 px-1">
                                <div className="btn-group btn-group-sm">
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm py-1 px-2"
                                    onClick={() =>
                                      handleRemoveLiftingRow(index)
                                    }
                                    disabled={
                                      liftingRows && liftingRows.length <= 7
                                    }
                                    title="Remove row"
                                  >
                                    <i className="bx bx-minus"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-success btn-sm py-1 px-2"
                                    onClick={handleAddLiftingRow}
                                    title="Add new row"
                                  >
                                    <i className="bx bx-plus"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  {/* Lifting Summary */}
                  <Row className="g-1 mb-2">
                    <Col md={6}>
                      <FormGroup>
                        <Label
                          className="form-label-sm text-muted"
                          style={{ fontSize: "0.65rem", fontWeight: "bold" }}
                        >
                          Contract Qty
                        </Label>
                        <div
                          className={`form-control-sm py-1 ${
                            state.totalLiftingQty > state.contractQty
                              ? "bg-warning"
                              : "bg-light"
                          }`}
                        >
                          {state.contractQty || state.Qty || "0.00"}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label
                          className="form-label-sm text-muted"
                          style={{ fontSize: "0.65rem", fontWeight: "bold" }}
                        >
                          Total Lifting
                        </Label>
                        <div
                          className={`form-control-sm py-1 ${
                            state.totalLiftingQty > state.contractQty
                              ? "bg-warning"
                              : "bg-light"
                          }`}
                        >
                          {state.totalLiftingQty || "0.00"}
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="g-1 mb-2">
                    <Col md={6}>
                      <FormGroup>
                        <Label
                          className="form-label-sm text-muted"
                          style={{ fontSize: "0.65rem", fontWeight: "bold" }}
                        >
                          Pending
                        </Label>
                        <div className="form-control-sm py-1 bg-light">
                          {Math.max(
                            0,
                            (state.contractQty || state.Qty || 0) -
                              (state.totalLiftingQty || 0)
                          ).toFixed(2)}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label
                          className="form-label-sm text-muted"
                          style={{ fontSize: "0.65rem", fontWeight: "bold" }}
                        >
                          Diff. Amt
                        </Label>
                        <input
                          type="text"
                          name="DiffAmt"
                          value={state.DiffAmt || "0.00"}
                          onChange={handleInputChange}
                          className={`form-control-sm py-1 ${
                            parseFloat(state.DiffAmt || 0) < 0
                              ? "text-success"
                              : parseFloat(state.DiffAmt || 0) > 0
                              ? "text-danger"
                              : ""
                          }`}
                          placeholder="0.00"
                          readOnly
                          disabled={state.isFieldsDisabled}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Back Seller Contract No */}
                  <Row className="g-1 mb-2">
                    <Col md={12}>
                      <FormGroup>
                        <Label
                          for="BackSellerCNo"
                          className="form-label-sm"
                          style={{ fontSize: "0.65rem", fontWeight: "bold" }}
                        >
                          Back Seller CNo
                        </Label>
                        <input
                          type="text"
                          name="BackSellerCNo"
                          id="BackSellerCNo"
                          value={state.BackSellerCNo || ""}
                          onChange={handleInputChange}
                          className="form-control form-control-sm"
                          placeholder="Back seller contract no"
                          disabled={state.isFieldsDisabled}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* Right Panel - Lifting Details and Notes */}
            </Row>

            {/* Action Buttons - Single Row */}
            <Row className="mt-2">
              <Col lg={12}>
                <div className="action-buttons d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => {
                        setState(prev => ({
                          ...prev,
                          isFieldsDisabled: !prev.isFieldsDisabled
                        }))
                      }}
                    >
                      <i className="bx bx-plus me-2"></i>
                      {state.isFieldsDisabled ? "Edit" : "Edit"}
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleSave}
                      disabled={state.isProgress || state.isFieldsDisabled}
                      ref={saveButtonRef}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          if (window.confirm("Are you sure you want to save?")) {
                            handleSave()
                          }
                        }
                      }}
                      style={{
                        transition: "all 0.3s ease",
                        boxShadow: "none"
                      }}
                      onFocus={e => {
                        e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 123, 255, 0.5), 0 0 15px rgba(0, 123, 255, 0.6)"
                        e.target.style.transform = "scale(1.05)"
                      }}
                      onBlur={e => {
                        e.target.style.boxShadow = "none"
                        e.target.style.transform = "scale(1)"
                      }}
                    >
                      {state.isProgress ? (
                        <>
                          <i className="bx bx-loader-alt bx-spin me-2"></i>
                          {state.isEditMode ? "Updating..." : "Saving..."}
                        </>
                      ) : (
                        <>
                          <i className="bx bx-save me-2"></i>
                          {state.isEditMode ? "Update" : "Save"}
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={handleDelete}
                    >
                      <i className="bx bx-trash me-2"></i>
                      Delete
                    </button>

                    <button
                      type="button"
                      className="btn btn-info btn-sm"
                      onClick={handlePrint}
                    >
                      <i className="bx bx-printer me-2"></i>
                      Print
                    </button>

                    <button
                      type="button"
                      className="btn btn-dark btn-sm"
                      onClick={handleExit}
                    >
                      <i className="bx bx-exit me-2"></i>
                      Exit
                    </button>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                    >
                      <i className="bx bx-envelope me-2"></i>
                      Mail Seller
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                    >
                      <i className="bx bx-envelope me-2"></i>
                      Mail Buyer
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm"
                    >
                      <i className="bx bxl-whatsapp me-2"></i>
                      WA Seller
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm"
                    >
                      <i className="bx bxl-whatsapp me-2"></i>
                      WA Buyer
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>

      {/* DoDetails Modal */}
      <Modal
        isOpen={doDetailsModal}
        toggle={() => setDoDetailsModal(false)}
        size="xl"
        centered
        zIndex={10000}
        modalClassName="do-details-modal"
        className="do-details-modal-class"
      >
        <ModalHeader toggle={() => setDoDetailsModal(false)}>
          <h5 className="modal-title">Contract DoDetails</h5>
        </ModalHeader>
        <ModalBody>
          {/* Contract Details Header */}
          <div 
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              color: "#333",
              textAlign: "center",
              padding: "10px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderRadius: "5px",
              marginBottom: "20px"
            }}
          >
            <span style={{ color: "#007bff" }}>ID: {state.DoDetails.Id}</span> | 
            <span style={{ color: "#28a745", marginLeft: "10px" }}>Contract: {state.DoDetails.ContractNo}</span> | 
            <span style={{ color: "#dc3545", marginLeft: "10px" }}>Date: {state.DoDetails.Date}</span> | 
            <span style={{ color: "#6f42c1", marginLeft: "10px" }}>{state.Type === 1 ? 'Seller' : 'Buyer'}: {state.DoDetails.Party}</span> | 
            <span style={{ color: "#fd7e14", marginLeft: "10px" }}>Broker: {state.DoDetails.Broker}</span> | 
            <span style={{ color: "#198754", marginLeft: "10px" }}>Type: {state.DoDetails.ContractType}</span> | 
            <span style={{ color: "#20c997", marginLeft: "10px" }}>Commodity: {state.DoDetails.Commodity}</span> | 
            <span style={{ color: "#e83e8c", marginLeft: "10px" }}>Qty: {state.DoDetails.Quantity}</span> | 
            <span style={{ color: "#6c757d", marginLeft: "10px" }}>Unit: {state.DoDetails.Unit}</span> | 
            <span style={{ color: "#17a2b8", marginLeft: "10px" }}>Vessel: {state.DoDetails.Vessel}</span> | 
            <span style={{ color: "#ffc107", marginLeft: "10px" }}>Port: {state.DoDetails.DeliveryPort}</span>
          </div>

          {/* Quantity Summary */}
          {(() => {
            const contractQty = parseFloat(state.DoDetails.Quantity || state.Qty || 0);
            let totalUsedQty = 0;
            state.DoDetailsArray.forEach((row) => {
              totalUsedQty += parseFloat(row.Qty || 0);
            });
            const remainingQty = contractQty - totalUsedQty;
            const isExceeded = totalUsedQty > contractQty;
            
            return (
              <div 
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px",
                  backgroundColor: isExceeded ? "#f8d7da" : "#d1ecf1",
                  border: `1px solid ${isExceeded ? "#f5c6cb" : "#bee5eb"}`,
                  borderRadius: "5px",
                  marginBottom: "15px",
                  textAlign: "center"
                }}
              >
                <span style={{ color: "#0c5460", marginRight: "20px" }}>
                  Contract Quantity: <strong>{contractQty}</strong> {state.DoDetails.Unit || ""}
                </span>
                <span style={{ color: "#856404", marginRight: "20px" }}>
                  Total Used: <strong>{totalUsedQty.toFixed(2)}</strong> {state.DoDetails.Unit || ""}
                </span>
                <span style={{ color: isExceeded ? "#dc3545" : "#155724" }}>
                  Remaining: <strong>{remainingQty.toFixed(2)}</strong> {state.DoDetails.Unit || ""}
                </span>
                {isExceeded && (
                  <span style={{ color: "#dc3545", marginLeft: "20px", fontSize: "12px" }}>
                    ⚠️ Exceeded by {Math.abs(remainingQty).toFixed(2)}!
                  </span>
                )}
              </div>
            );
          })()}

          {/* DoDetails Table */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
              <h6 style={{ color: "#333", margin: 0 }}>DO Details Entry</h6>
              <Button
                color="success"
                size="sm"
                onClick={handleAddNewDoDetailsRow}
                disabled={editingRowIndex !== null}
                title="Add New Row"
              >
                <i className="bx bx-plus" style={{ fontSize: "18px", marginRight: "5px" }}></i>
                Add Row
              </Button>
            </div>
            <Table striped bordered hover responsive size="sm">
              <thead style={{ backgroundColor: "#e9ecef" }}>
                <tr>
                  <th style={{ minWidth: "120px" }}>DO No</th>
                  <th style={{ minWidth: "120px" }}>DO Date</th>
                  <th style={{ minWidth: "100px" }}>Quantity</th>
                  <th style={{ minWidth: "100px" }}>Tank No</th>
                  <th style={{ minWidth: "150px" }}>Transporter Name</th>
                  <th style={{ minWidth: "150px" }}>TERMINAL NAME</th>
                  <th style={{ minWidth: "120px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.DoDetailsArray.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <Input
                        type="text"
                        value={row.DoNo || ""}
                        onChange={(e) => handleDoDetailsInputChange(index, "DoNo", e.target.value)}
                        disabled={editingRowIndex !== null && editingRowIndex !== index && row.Id}
                        size="sm"
                        style={{ minWidth: "100px" }}
                        ref={index === 0 ? doNoInputRef : null}
                      />
                    </td>
                    <td>
                      <Input
                        type="date"
                        value={convertApiDateToInputFormat(row.DoDate) || ""}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          handleDoDetailsInputChange(index, "DoDate", selectedDate);
                        }}
                        disabled={false}
                        size="sm"
                        style={{ minWidth: "100px" }}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        value={row.Qty || ""}
                        onChange={(e) => handleDoDetailsInputChange(index, "Qty", e.target.value)}
                        disabled={editingRowIndex !== null && editingRowIndex !== index && row.Id}
                        size="sm"
                        style={{ minWidth: "80px" }}
                      />
                    </td>
                    <td>
                      <Input
                        type="text"
                        value={row.TankNo || ""}
                        onChange={(e) => handleDoDetailsInputChange(index, "TankNo", e.target.value)}
                        disabled={editingRowIndex !== null && editingRowIndex !== index && row.Id}
                        size="sm"
                        style={{ minWidth: "80px" }}
                      />
                    </td>
                    <td>
                      <Input
                        type="text"
                        value={row.TransName || ""}
                        onChange={(e) => handleDoDetailsInputChange(index, "TransName", e.target.value)}
                        disabled={editingRowIndex !== null && editingRowIndex !== index && row.Id}
                        size="sm"
                        style={{ minWidth: "130px" }}
                        placeholder="Enter Transporter Name"
                      />
                    </td>
                    <td>
                      <Input
                        type="text"
                        value={row.LogisticsFirm || ""}
                        onChange={(e) => handleDoDetailsInputChange(index, "LogisticsFirm", e.target.value)}
                        disabled={editingRowIndex !== null && editingRowIndex !== index && row.Id}
                        size="sm"
                        style={{ minWidth: "130px" }}
                        placeholder="Enter Logistics Firm"
                      />
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                        {row.Id ? (
                          // Existing row with ID
                          editingRowIndex === index ? (
                            <>
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => handleSaveUpdateDoDetails(index)}
                              >
                                Update
                              </Button>
                              <Button
                                color="secondary"
                                size="sm"
                                onClick={handleCancelEdit}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => handleEditDoDetails(index)}
                                disabled={editingRowIndex !== null}
                              >
                                Edit
                              </Button>
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => handlePrintDoDetails(index)}
                                disabled={editingRowIndex !== null}
                                title="Print DO Details"
                              >
                                <i className="bx bx-printer"></i>
                              </Button>
                            </>
                          )
                        ) : (
                          // New row without ID
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleSaveUpdateDoDetails(index)}
                          >
                            Save
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setDoDetailsModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Commodity Modal */}
      <Modal
        isOpen={commodityModal}
        toggle={toggleCommodityModal}
        size="md"
        centered
      >
        <ModalHeader toggle={toggleCommodityModal}>
          <h5 className="modal-title">Add New Commodity</h5>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="newCommodityName" className="form-label-sm">
              Commodity Type *
            </Label>
            <Input
              type="text"
              id="newCommodityName"
              value={newCommodityData.Name}
              onChange={e =>
                handleCommodityModalInputChange("Name", e.target.value)
              }
              placeholder="Enter commodity type"
              className="form-control-sm"
            />
          </FormGroup>
          <FormGroup>
            <Label for="newCommodityAlias" className="form-label-sm">
              Alias
            </Label>
            <Input
              type="text"
              id="newCommodityAlias"
              value={newCommodityData.Alias}
              onChange={e =>
                handleCommodityModalInputChange("Alias", e.target.value)
              }
              placeholder="Enter alias"
              className="form-control-sm"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleCommodityModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSaveNewCommodity}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>
     
    </div>
  )
}

export default EditContract
